import React, {useState} from 'react'
import {Prissoknad} from '../../api/prissoknader'
import PrissoknadSteg from "./PrissoknadSteg"
import Modal from "../Modal/Modal"
import FileUploadForPrissoknad from "../FileUploadForPrissoknad"
import {PktButton} from "@oslokommune/punkt-react"
import styles from "./styles.module.scss"
import FileList from "../FileList"
import DownloadPdfLink from "./DownloadPdfLink";

type PrissoknadStatusProps = React.HTMLAttributes<HTMLDivElement> & {
  prissoknad: Prissoknad
}

const ReferansenummerMedNedlastingsknapp = ({brukerreferanse}: { brukerreferanse: string }) => {
  return (
    <div className={styles.refNrContainer}>
      <span className={'pkt-txt-14'}>Referansenummer: {brukerreferanse}</span>
      <DownloadPdfLink brukerref={brukerreferanse}/>
    </div>
  )
}

const PrissoknadStatus = ({prissoknad, className}: PrissoknadStatusProps) => {
  const soknadDate = prissoknad.innsendtdato ? new Date(prissoknad.innsendtdato) : undefined
  const soknadDateString = soknadDate?.toLocaleString("no-NB", {dateStyle: "long"})
  const soknadYear = soknadDate?.getFullYear() ?? NaN
  const [showUpload, setShowUpload] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<Array<File>>([])

  const FileUploadInfo = () => {
    const files = [...(prissoknad.brukeropplastetDokumentasjon ?? []), ...uploadedFiles]
    return <>
      {prissoknad.søknadstype === "RESØKNAD" && <p>
        Vi henter informasjon om familiens (husholdningen) inntekt fra i fjor automatisk fra Skatteetaten.
        Har familien fått en vesentlig nedgang i inntekt i år, kan du legge ved dokumentasjon.
      </p>}
      {prissoknad.søknadstype !== "RESØKNAD" && <p>
        Har du fått beskjed fra oss om å legge til ekstra dokumentasjon, kan du gjøre det her.
      </p>}

      <PktButton
        skin="tertiary"
        size="small"
        iconName="plus-circle"
        onClick={() => setShowUpload(true)}
        variant="icon-left"
        className={styles.uploadButton}>

        Send inn dokumentasjon

      </PktButton>
      {files.length > 0 && <>
        <p className="mt-size-16 mb-size-4">Disse filene er lagt til søknaden:</p>
        <FileList
          showFileSize={false}
          files={files}
          style={{width: "100%"}}
          className="pkt-txt-16 mt-size-0"
        />
      </>}
    </>

  }
  return (
    <div className={className}>
      <PrissoknadSteg title={'Søknaden er mottatt'}
                      state={prissoknad.søknadsstatus == "VENTER_PÅ_SKATTEOPPGJØR" ? "current" : "past"}>

        {prissoknad.søknadsstatus === "VENTER_PÅ_SKATTEOPPGJØR" &&
          <ReferansenummerMedNedlastingsknapp brukerreferanse={prissoknad.brukerreferanse}/>
        }
        {prissoknad.søknadstype !== "RESØKNAD" &&
          <>
            <p>

              Søknad om redusert barnehagepris ble mottatt {soknadDateString}.
              Vi henter informasjon om familiens (husholdningen) inntekt automatisk fra Skatteetaten.

            </p>
          </>
        }
        {prissoknad.søknadstype === "RESØKNAD" &&
          <>
            <p>

              Søknad om redusert barnehagepris ble automatisk sendt inn av Oslo
              kommune {soknadDateString}.
              Søknaden gjelder for barnehageåret {soknadYear}/{soknadYear + 1}.

            </p>
          </>
        }
        {prissoknad.søknadsstatus === "VENTER_PÅ_SKATTEOPPGJØR" && prissoknad.søknadstype === "RESØKNAD" &&
          <FileUploadInfo/>
        }
        <p>

          Har du spørsmål,{' '}
          <a
            href={'https://www.oslo.kommune.no/barnehage/kontakt-barnehageadministrasjonen/'}
            className="pkt-link"
            target="_blank"
            rel="noreferrer"
          >
            kontakt Oslo kommunes kontaktsenter for barnehage
          </a>.

        </p>
      </PrissoknadSteg>
      <PrissoknadSteg title={'Søknaden er under behandling'}
                      state={prissoknad.søknadsstatus == "OVERSENDT_SAKSBEHANDLER" ? "current" : prissoknad.søknadsstatus === "VENTER_PÅ_SKATTEOPPGJØR" ? "future" : "past"}>

        {prissoknad.søknadsstatus === "OVERSENDT_SAKSBEHANDLER" &&
          <ReferansenummerMedNedlastingsknapp brukerreferanse={prissoknad.brukerreferanse}/>
        }
        <p>

          Vi behandler nå søknaden din. Svaret på søknaden blir sendt til din digitale postkasse (Altinn,
          Digipost eller e-Boks).

        </p>
        {prissoknad.søknadsstatus === "OVERSENDT_SAKSBEHANDLER" &&
          <FileUploadInfo/>
        }
        <p>

          Har du spørsmål,{' '}
          <a
            href={'https://www.oslo.kommune.no/barnehage/kontakt-barnehageadministrasjonen/'}
            className="pkt-link"
            target="_blank"
            rel="noreferrer"
          >
            kontakt Oslo kommunes kontaktsenter for barnehage
          </a>.

        </p>
      </PrissoknadSteg>
      <PrissoknadSteg title={'Søknaden er ferdig behandlet'}
                      state={prissoknad.søknadsstatus === "FERDIGBEHANDLET" ? 'current' : 'future'} lastStep={true}>

        {prissoknad.søknadsstatus === "FERDIGBEHANDLET" &&
          <ReferansenummerMedNedlastingsknapp brukerreferanse={prissoknad.brukerreferanse}/>
        }
        <p>

          Du får varsel på e-post eller SMS når svaret er tilgjengelig i din digitale postkasse. Har du
          reservert deg mot digital post, kommer svaret som brev i posten.

        </p>
        <p>

          Har du spørsmål om søknaden,{' '}
          <a
            href={'https://www.oslo.kommune.no/barnehage/kontakt-barnehageadministrasjonen/'}
            className="pkt-link"
            target="_blank"
            rel="noreferrer"
          >
            kontakt Oslo kommunes kontaktsenter for barnehage
          </a>.

        </p>

      </PrissoknadSteg>
      <Modal show={showUpload}
             onClose={() => setShowUpload(false)}
             style={{
               width: "100%",
               maxWidth: "601px"
             }}>
        {/*todo: clean up styles*/}
        <div style={{
          background: "#fff",
          borderRadius: "5px",
          boxShadow: "2px 2px 3px #ccc",
          padding: "20px",
          width: "100%",
        }}>
          <FileUploadForPrissoknad
            brukerref={prissoknad.brukerreferanse}
            onCompleted={files => {
              setUploadedFiles([...uploadedFiles, ...files])
            }}
            onClose={() => {
              setShowUpload(false)
            }}
          />
        </div>
      </Modal>
    </div>
  )
}

export default PrissoknadStatus
